import { useApolloClient } from "@apollo/client/react";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { Box, TableRow, Typography } from "@mui/material";
import { PropTypes } from "prop-types";
import { useTranslation } from "react-i18next";

import { TableList } from "@/atoms";
import { ReviewCards } from "@/components";
import { identifiersData, namesData, tableHeadCellsData, urlsData } from "@/data";
import { releaseNameFragment } from "@/graph";
import { StyledLink, StyledTableCell } from "@/styles";

const BulkDistributionResults = ({ results }) => {
    const { t } = useTranslation();

    const client = useApolloClient();

    const failedResults = results.filter(({ success }) => !success);

    // a video that could not be queued gets its own row, while a release that could not be queued at all has no video
    const hasVideos = failedResults.some(({ videoId }) => videoId);

    const { link: linkId, table: tableId } = identifiersData.ids;

    const getReleaseHandler = (releaseId) =>
        client.readFragment({
            fragment: releaseNameFragment,
            id: client.cache.identify({ __typename: "Release", id: releaseId }),
        });

    return (
        <Box
            sx={{
                mx: 2,
            }}
        >
            <ReviewCards
                failedLabel={t("labels.failedToStart")}
                failedNumber={failedResults.length}
                successLabel={t("labels.successfullyStarted")}
                successNumber={results.length - failedResults.length}
            />
            <TableList
                headCells={tableHeadCellsData.bulkDistributionResults[hasVideos ? "videos" : "releases"]}
                isPaginated={false}
            >
                {failedResults.map(({ message, releaseId, title, videoId }, index) => {
                    const release = getReleaseHandler(releaseId);

                    return (
                        <TableRow key={`${releaseId}_${videoId}`} tabIndex={-1} hover>
                            <StyledTableCell boldCell={+true}>
                                {/* a new tab keeps these results open while the release is being fixed */}
                                <StyledLink
                                    id={`${tableId}_${namesData.links.release}_${linkId}_${index + 1}`}
                                    target="_blank"
                                    to={`${urlsData.routes.releases.url}/${releaseId}`}
                                >
                                    {release?.album_name || `${t("labels.release")} #${releaseId}`}
                                </StyledLink>
                                {release?.upc && (
                                    <Typography color="textSecondary" component="p" variant="caption">
                                        {release.upc}
                                    </Typography>
                                )}
                            </StyledTableCell>
                            {hasVideos && <StyledTableCell>{videoId ? title : "-"}</StyledTableCell>}
                            {/* the shared cell caps every column at 200px, so the message would be squeezed into as
                            narrow a column as a short release name. uncapped, it takes the width the others leave */}
                            <StyledTableCell style={{ maxWidth: "none" }}>
                                <Box
                                    sx={{
                                        alignItems: "flex-start",
                                        display: "flex",
                                        gap: 1,
                                    }}
                                >
                                    <ReportProblemIcon color="error" fontSize="small" />
                                    {/* the cell's nowrap would stretch the column to the full message and push it past
                                    the drawer, so the message wraps onto as many lines as it needs instead */}
                                    <Typography
                                        color="error"
                                        variant="caption"
                                        sx={{
                                            overflowWrap: "anywhere",
                                            whiteSpace: "normal",
                                        }}
                                    >
                                        {message}
                                    </Typography>
                                </Box>
                            </StyledTableCell>
                        </TableRow>
                    );
                })}
            </TableList>
        </Box>
    );
};

export default BulkDistributionResults;

BulkDistributionResults.propTypes = {
    results: PropTypes.array,
};
